import { ApplicationInsights, type ITelemetryItem } from "@microsoft/applicationinsights-web";
import { type Config } from "@/config";
import type { AppInsightsPluginOptions } from "vue3-application-insights";
import type { Router } from "vue-router";

/**
 * @type {ApplicationInsights | null}
 */
let appInsightsInstance: ApplicationInsights | null = null;

export const getAppInsights = () => {
  if (!appInsightsInstance) {
    console.warn("getAppInsights() was called before the Application Insights instance was set.");
  }
  return appInsightsInstance;
};

// Escape special regex characters in a string
function escapeRegExp(string: string): string {
  return string.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
}

let contentRegex: RegExp;
let apiRegex: RegExp;

function telemetryInitializer(envelope: ITelemetryItem): boolean | void {
  if (envelope.baseType !== "RemoteDependencyData") {
    return true;
  }

  const telemetryItem = { ...envelope.baseData, ...envelope.data } as any;

  // Don't log options
  if (telemetryItem.name.toUpperCase().startsWith("OPTIONS ")) {
    return false;
  }

  // Identify content server retries and remove them from the logs
  if (telemetryItem.responseCode === 503 && contentRegex!.test(telemetryItem.target)) {
    return false;
  }

  // Track unity api 404s and prevent the logging of the 404, any real 404s are
  // logged in the axios worker
  if (telemetryItem.responseCode === 404 && apiRegex!.test(telemetryItem.target)) {
    return false;
  }

  return true;
}

export function createInsightsSettings(config: Config, router: Router): AppInsightsPluginOptions {
  contentRegex = new RegExp(escapeRegExp(config.contentUrl));
  apiRegex = new RegExp(escapeRegExp(config.apiUrl));

  const applicationInsights = new ApplicationInsights({
    config: {
      instrumentationKey: config.instrumentationKey!,
      enableAutoRouteTracking: true, // Optionally track route changes
      maxBatchInterval: 0, // Send telemetry immediately
      disableFetchTracking: false, // Enable fetch tracking
      enableCorsCorrelation: true, // Enable CORS correlation
      enableRequestHeaderTracking: true, // Enable request header tracking
      enableResponseHeaderTracking: true, // Enable response header tracking
      disableAjaxTracking: false, // Disable AJAX tracking
      // DO NOT DISABLE the ajax tracking, we ARE tracking manually
      // BUT without the automatic tracking, there are NO correlation headers added
      isCookieUseDisabled: false, // Enable cookie usage,
      // Disable the deprecated 'unload' event to avoid jQuery 3.7.1+ deprecation warnings
      // This also prevents Chrome's warnings about the unload event
      disablePageUnloadEvents: ["unload"],
    },
  });

  const aiOptions = {
    appInsightsInstance: applicationInsights,
    router: router,
    appName: config.app, // For route event to be like: [appName] <route.name>
    trackAppErrors: true,
    cloudRole: `${config.app}${!!config.instrumentationEnvironment ? `-${config.instrumentationEnvironment || "Local"}` : ""}`,

    onLoaded: (appInsights: ApplicationInsights) => {
      // Custom changes for application insights
      // Store a shared instance
      appInsightsInstance = appInsights;

      appInsights.addTelemetryInitializer(telemetryInitializer);
    },
  };

  return aiOptions;
}
